import { Anchor, Badge, Group, Text } from "@mantine/core";
import { Mono, ExternalMono } from "@/components/Mono";
import { Timestamp } from "@/components/Timestamp";
import { RelatedLink } from "@/components/RelatedLink";
import { AdminActionButton } from "@/components/AdminActionButton";
import { grantKycRetry, syncKycVerification } from "@/api/actions";
import { fullName, humanize, chainName } from "@/lib/format";
import { addressUrl } from "@/lib/explorer";
import { COLLECTIONS } from "@/firestore/collections";
import type { KycVerificationDoc, UserDoc, WalletDoc, WithDocId } from "@/firestore/types";
import type { EntityConfig } from "../types";
import { monoCol, relatedCol, stateCol, textCol, timeCol } from "../shared";
import { col } from "../columns";
import { GenerationBadge } from "@/components/GenerationBadge";

type P<T> = WithDocId<T>;

function RolesBadges({ roles }: { roles: string[] }) {
  if (!roles || roles.length === 0) return <Text c="dimmed">—</Text>;
  return (
    <Group gap={4}>
      {roles.map((r) => (
        <Badge key={r} color={r === "admin" ? "indigo" : "gray"} variant="light" size="sm">
          {r}
        </Badge>
      ))}
    </Group>
  );
}

export const userConfig: EntityConfig<UserDoc> = {
  key: "user",
  collection: COLLECTIONS.users,
  title: "User",
  pluralTitle: "Users",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<UserDoc>>("id", "ID", (r) => r.id),
    textCol<P<UserDoc>>("name", "Name", (r) => fullName(r.firstName, r.lastName, r.handle)),
    textCol<P<UserDoc>>("email", "Email", (r) => r.primaryEmail),
    textCol<P<UserDoc>>("handle", "Handle", (r) => (r.handle ? `@${r.handle}` : null)),
    {
      id: "roles",
      header: "Roles",
      accessorFn: (r: P<UserDoc>) => (r.roles ?? []).join(","),
      cell: (ctx) => <RolesBadges roles={ctx.row.original.roles ?? []} />,
    },
    timeCol<P<UserDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Name", value: fullName(d.firstName, d.lastName, d.handle) },
    { label: "Email", value: d.primaryEmail ?? "—" },
    { label: "Handle", value: d.handle ? `@${d.handle}` : "—" },
    { label: "Roles", value: <RolesBadges roles={d.roles ?? []} /> },
    { label: "Created", value: <Timestamp value={d.createdAt} absolute /> },
    { label: "Updated", value: <Timestamp value={d.updatedAt} absolute /> },
  ],
};

export const walletConfig: EntityConfig<WalletDoc> = {
  key: "wallet",
  collection: COLLECTIONS.wallets,
  title: "Wallet",
  pluralTitle: "Wallets",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<WalletDoc>>("id", "ID", (r) => r.id),
    stateCol<P<WalletDoc>>((r) => r.state),
    col<P<WalletDoc>>({
      id: "generation",
      header: "Generation",
      value: (r) => r.contractGeneration ?? "",
      cell: (r) => <GenerationBadge generation={r.contractGeneration} />,
    }),
    monoCol<P<WalletDoc>>("main", "Main", (r) => r.mainAddress),
    monoCol<P<WalletDoc>>("reserve", "Reserve", (r) => r.reserveAddress),
    textCol<P<WalletDoc>>("chain", "Chain", (r) => (r.chainId ? String(r.chainId) : null)),
    relatedCol<P<WalletDoc>>("owner", "User", "user", (r) => r.owner),
    // `createdAt` is the apps' selection timestamp (the epoch on a retired wallet).
    timeCol<P<WalletDoc>>("createdAt", "Created", (r) => r.originalCreatedAt ?? r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    { label: "Contract generation", value: <GenerationBadge generation={d.contractGeneration} /> },
    { label: "Chain", value: chainName(d.chainId) },
    {
      label: "Main address",
      value: <ExternalMono value={d.mainAddress} href={addressUrl(d.mainAddress, d.chainId)} />,
    },
    {
      label: "Reserve address",
      value: (
        <ExternalMono value={d.reserveAddress} href={addressUrl(d.reserveAddress, d.chainId)} />
      ),
    },
    { label: "Migrated from", value: <RelatedLink entity="wallet" id={d.migratedFromWallet} /> },
    { label: "Migrated to", value: <RelatedLink entity="wallet" id={d.migratedToWallet} /> },
    { label: "Root wallet", value: <RelatedLink entity="wallet" id={d.rootWallet} /> },
    { label: "Created", value: <Timestamp value={d.originalCreatedAt ?? d.createdAt} absolute /> },
  ],
};

export const kycVerificationConfig: EntityConfig<KycVerificationDoc> = {
  key: "kycVerification",
  collection: COLLECTIONS.kycVerifications,
  title: "KYC Verification",
  pluralTitle: "KYC",
  ownerField: "user",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<KycVerificationDoc>>("id", "ID", (r) => r.id),
    stateCol<P<KycVerificationDoc>>((r) => r.state),
    textCol<P<KycVerificationDoc>>("provider", "Provider", (r) => r.provider, { humanize: true }),
    relatedCol<P<KycVerificationDoc>>("user", "User", "user", (r) => r.user),
    timeCol<P<KycVerificationDoc>>("decidedAt", "Decided", (r) => r.decidedAt),
    timeCol<P<KycVerificationDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Provider", value: humanize(d.provider) },
    { label: "User", value: <RelatedLink entity="user" id={d.user} /> },
    {
      label: "Provider report",
      value: d.providerReportUrl ? (
        <Anchor href={d.providerReportUrl} target="_blank" rel="noreferrer" size="sm">
          Open report ↗
        </Anchor>
      ) : (
        "—"
      ),
    },
    { label: "Resubmits", value: <RelatedLink entity="kycVerification" id={d.resubmits} /> },
    { label: "Started", value: <Timestamp value={d.startedAt} absolute /> },
    { label: "Submitted", value: <Timestamp value={d.submittedAt} absolute /> },
    { label: "Decided", value: <Timestamp value={d.decidedAt} absolute /> },
    { label: "Next action", value: humanize(d.nextAction ?? "unknown") },
    { label: "Rejection reason", value: d.rejectionReason ?? "—", full: true },
  ],
  actions: (d) => (
    <Group>
      <AdminActionButton action={syncKycVerification} params={{ id: d.id }} />
      {d.state === "rejected" && d.nextAction !== "retry" ? (
        <AdminActionButton
          key={d.updatedAt}
          action={grantKycRetry}
          params={{ id: d.id, updatedAt: d.updatedAt }}
        />
      ) : d.nextAction === "retry" ? (
        <Text size="sm" c="teal">
          One attempt available — no additional grant needed.
        </Text>
      ) : null}
    </Group>
  ),
};
